<template>
	<div class="landing" :class="{ 'is-mobile': !isPc }">
		<div v-if="!pageReady" class="landing-sk-overlay">
			<LdSkeleton variant="landing" />
		</div>
		<div class="landing-page">
			<section class="hero">
				<div class="hero-copy">
					<div class="eyebrow">{{ $t('landing.eyebrow') }}</div>
					<h1 class="hero-title">{{ $t('landing.heroTitle') }}</h1>
					<p class="hero-subtitle">{{ $t('landing.heroSubtitle') }}</p>
					<div class="hero-actions">
						<div class="cta-primary" role="button" @click="goMarket">{{ $t('landing.ctaExplore') }}</div>
						<div class="cta-ghost" role="button" @click="goJoin">{{ $t('lendingMenu.getStarted') }}</div>
					</div>
				</div>
				<div class="hero-visual">
					<LandingVisual kind="hero" :compact="!isPc" />
					<div class="float-pill pill-a" v-if="floatPills[0]">
						<span class="pill-label">{{ floatPills[0].code }}</span>
						<span class="pill-value" :class="floatPills[0].up ? 'is-up' : 'is-down'">{{ floatPills[0].change }}</span>
					</div>
					<div class="float-pill pill-b" v-if="floatPills[1]">
						<span class="pill-label">{{ floatPills[1].code }}</span>
						<span class="pill-value" :class="floatPills[1].up ? 'is-up' : 'is-down'">{{ floatPills[1].change }}</span>
					</div>
				</div>
			</section>

			<div class="ticker" :class="{ 'is-pairs': tickerPairs.length }">
				<div class="ticker-track">
					<template v-if="tickerPairs.length">
						<div class="ticker-pair" v-for="(item, idx) in tickerLoop" :key="item.code + '-' + idx">
							<span class="ticker-code">{{ item.code }}</span>
							<span class="ticker-price">{{ item.price }}</span>
							<span class="ticker-chg" :class="item.up ? 'is-up' : 'is-down'">{{ item.change }}</span>
						</div>
					</template>
					<template v-else>
						<span class="ticker-copy">{{ $t('landing.ticker') }}</span>
						<span class="ticker-copy">{{ $t('landing.ticker') }}</span>
					</template>
				</div>
			</div>

			<section class="stats">
				<div class="stat-cell">
					<div class="stat-value">{{ $t('landing.statAssetsValue') }}</div>
					<div class="stat-label">{{ $t('landing.statAssets') }}</div>
				</div>
				<div class="stat-cell">
					<div class="stat-value">{{ $t('landing.productOptionsTitle') }}</div>
					<div class="stat-label">{{ $t('landing.statOptionsHint') }}</div>
				</div>
				<div class="stat-cell">
					<div class="stat-value">{{ $t('landing.productContractsTitle') }}</div>
					<div class="stat-label">{{ $t('landing.statContractsHint') }}</div>
				</div>
				<div class="stat-cell">
					<div class="stat-value">{{ $t('landing.statSupportValue') }}</div>
					<div class="stat-label">{{ $t('landing.statSupport') }}</div>
				</div>
			</section>

			<section class="block">
				<div class="block-head">
					<h2 class="block-title">{{ $t('landing.productsTitle') }}</h2>
					<p class="block-desc">{{ $t('landing.productsDesc') }}</p>
				</div>
				<div class="product-featured">
					<div
						class="product-card is-featured"
						v-for="item in featuredProducts"
						:key="item.key"
						role="button"
						@click="item.go()"
					>
						<div class="product-media">
							<img class="product-cover" :src="coverOf(item.key)" alt="" />
						</div>
						<div class="product-body">
							<div class="product-title-row">
								<h3 class="product-title">{{ $t(item.titleKey) }}</h3>
								<svg class="product-arrow" width="18" height="18" viewBox="0 0 20 20" fill="none" aria-hidden="true">
									<path d="M5 10h10M11 5l5 5-5 5" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
								</svg>
							</div>
							<p class="product-desc">{{ $t(item.descKey) }}</p>
						</div>
					</div>
				</div>
				<div class="product-grid">
					<div
						class="product-card"
						v-for="item in marketProducts"
						:key="item.key"
						role="button"
						@click="item.go()"
					>
						<div class="product-media is-compact">
							<img class="product-cover" :src="coverOf(item.key)" alt="" />
						</div>
						<div class="product-body">
							<div class="product-title-row">
								<h3 class="product-title">{{ $t(item.titleKey) }}</h3>
								<svg class="product-arrow" width="18" height="18" viewBox="0 0 20 20" fill="none" aria-hidden="true">
									<path d="M5 10h10M11 5l5 5-5 5" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
								</svg>
							</div>
							<p class="product-desc">{{ $t(item.descKey) }}</p>
						</div>
					</div>
				</div>
			</section>

			<section class="block markets-block">
				<div class="markets-copy">
					<div class="eyebrow">{{ $t('landing.marketsEyebrow') }}</div>
					<h2 class="block-title">{{ $t('landing.marketsTitle') }}</h2>
					<p class="block-desc">{{ $t('landing.marketsDesc') }}</p>
					<ul class="about-points">
						<li v-for="key in marketPoints" :key="key">{{ $t(key) }}</li>
					</ul>
					<div class="text-link" role="button" @click="goMarket">{{ $t('landing.ctaExplore') }}</div>
				</div>
				<div class="markets-media">
					<img class="product-cover" :src="coverOf('markets')" alt="" />
				</div>
			</section>

			<section class="block smart-block">
				<div class="smart-copy">
					<div class="eyebrow">{{ $t('landing.smartEyebrow') }}</div>
					<h2 class="block-title">{{ $t('landing.smartTitle') }}</h2>
					<p class="block-desc">{{ $t('landing.smartDesc') }}</p>
					<ul class="about-points">
						<li v-for="key in smartPoints" :key="key">{{ $t(key) }}</li>
					</ul>
					<div class="text-link" role="button" @click="goTrading">{{ $t('landing.ctaSmart') }}</div>
				</div>
				<div class="smart-media">
					<img class="product-cover" :src="coverOf('smart')" alt="" />
				</div>
			</section>

			<section class="block">
				<div class="block-head">
					<h2 class="block-title">{{ $t('landing.toolsTitle') }}</h2>
					<p class="block-desc">{{ $t('landing.toolsDesc') }}</p>
				</div>
				<div class="product-grid is-tools">
					<div
						class="product-card"
						v-for="item in toolProducts"
						:key="item.key"
						role="button"
						@click="item.go()"
					>
						<div class="product-media is-compact">
							<img class="product-cover" :src="coverOf(item.key)" alt="" />
						</div>
						<div class="product-body">
							<div class="product-title-row">
								<h3 class="product-title">{{ $t(item.titleKey) }}</h3>
								<svg class="product-arrow" width="18" height="18" viewBox="0 0 20 20" fill="none" aria-hidden="true">
									<path d="M5 10h10M11 5l5 5-5 5" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
								</svg>
							</div>
							<p class="product-desc">{{ $t(item.descKey) }}</p>
						</div>
					</div>
				</div>
			</section>

			<section class="block">
				<div class="block-head">
					<h2 class="block-title">{{ $t('landing.howTitle') }}</h2>
					<p class="block-desc">{{ $t('landing.howDesc') }}</p>
				</div>
				<div class="how-grid">
					<div class="how-card" v-for="step in howSteps" :key="step.n">
						<div class="how-icon" v-html="step.icon"></div>
						<div class="how-index">{{ step.n }}</div>
						<h3 class="how-title">{{ $t(step.titleKey) }}</h3>
						<p class="how-desc">{{ $t(step.descKey) }}</p>
					</div>
				</div>
			</section>

			<section class="block security-block">
				<div class="security-media">
					<img class="product-cover" :src="coverOf('security')" alt="" />
				</div>
				<div class="security-copy">
					<div class="eyebrow">{{ $t('landing.securityEyebrow') }}</div>
					<h2 class="block-title">{{ $t('landing.securityTitle') }}</h2>
					<p class="block-desc">{{ $t('landing.securityDesc') }}</p>
					<div class="sec-list">
						<div class="sec-item" v-for="item in securityItems" :key="item.titleKey">
							<div class="sec-icon" v-html="item.icon"></div>
							<div>
								<div class="sec-title">{{ $t(item.titleKey) }}</div>
								<div class="sec-desc">{{ $t(item.descKey) }}</div>
							</div>
						</div>
					</div>
				</div>
			</section>

			<section class="block about-block">
				<div class="about-copy">
					<h2 class="block-title">{{ $t('landing.aboutTitle') }}</h2>
					<p class="about-body">{{ $t('landing.aboutBody') }}</p>
					<ul class="about-points">
						<li v-for="key in aboutPoints" :key="key">{{ $t(key) }}</li>
					</ul>
				</div>
				<div class="about-panel">
					<div class="about-visual">
						<img class="product-cover" :src="coverOf('about')" alt="" />
					</div>
					<div class="about-metric" v-for="m in aboutMetrics" :key="m.labelKey">
						<div class="about-metric-label">{{ $t(m.labelKey) }}</div>
						<div class="about-metric-value">{{ m.value }}</div>
					</div>
				</div>
			</section>

			<section class="final-cta">
				<h2 class="final-title">{{ $t('landing.finalTitle') }}</h2>
				<p class="final-desc">{{ $t('landing.finalDesc') }}</p>
				<div class="hero-actions final-actions">
					<div class="cta-primary" role="button" @click="goJoin">{{ $t('lendingMenu.getStarted') }}</div>
					<div class="cta-ghost" role="button" @click="goMarket">{{ $t('landing.ctaExplore') }}</div>
				</div>
			</section>
		</div>
	</div>
</template>

<script>
import { mapState } from 'vuex'
import { requireLogin } from '@/utils/lendingGetStarted'
import { setMarketScope, clearMarketScope, MARKET_TYPE_CRYPTO } from '@/utils/market-ws'
import LandingVisual from './LandingVisual.vue'

const ICONS = {
	audit: '<svg width="20" height="20" viewBox="0 0 20 20" fill="none"><path d="M5.5 3.5h6.2L15.5 7.3v9.2a1 1 0 0 1-1 1h-9a1 1 0 0 1-1-1v-12a1 1 0 0 1 1-1z" stroke="currentColor" stroke-width="1.4"/><path d="M11.5 3.5V7.5H15.5" stroke="currentColor" stroke-width="1.4"/><path d="M7 10.5h6M7 13.5h4" stroke="currentColor" stroke-width="1.4" stroke-linecap="round"/></svg>',
	shield: '<svg width="20" height="20" viewBox="0 0 20 20" fill="none"><path d="M10 2.5L15.5 5v4.2c0 3.5-2.3 6.6-5.5 7.8-3.2-1.2-5.5-4.3-5.5-7.8V5L10 2.5z" stroke="currentColor" stroke-width="1.4" stroke-linejoin="round"/><path d="M8 10.2l1.4 1.4L12.4 8.6" stroke="currentColor" stroke-width="1.4" stroke-linecap="round" stroke-linejoin="round"/></svg>',
	eye: '<svg width="20" height="20" viewBox="0 0 20 20" fill="none"><path d="M2.8 10s2.6-5 7.2-5 7.2 5 7.2 5-2.6 5-7.2 5-7.2-5-7.2-5z" stroke="currentColor" stroke-width="1.4"/><circle cx="10" cy="10" r="2.2" stroke="currentColor" stroke-width="1.4"/></svg>',
	support: '<svg width="20" height="20" viewBox="0 0 20 20" fill="none"><path d="M4 4.5h12a1 1 0 0 1 1 1v6.5a1 1 0 0 1-1 1H8.8L5 15.5V5.5a1 1 0 0 1 1-1z" stroke="currentColor" stroke-width="1.4" stroke-linejoin="round"/><path d="M7 8.5h6M7 11h4" stroke="currentColor" stroke-width="1.4" stroke-linecap="round"/></svg>',
	account: '<svg width="22" height="22" viewBox="0 0 20 20" fill="none"><circle cx="10" cy="7" r="2.6" stroke="currentColor" stroke-width="1.4"/><path d="M4.8 16c.8-2.6 2.6-4 5.2-4s4.4 1.4 5.2 4" stroke="currentColor" stroke-width="1.4" stroke-linecap="round"/></svg>',
	market: '<svg width="22" height="22" viewBox="0 0 20 20" fill="none"><path d="M3.5 14.5l4-4.2 3 2.6 6-6.4" stroke="currentColor" stroke-width="1.4" stroke-linecap="round" stroke-linejoin="round"/><path d="M13 6.5h3.5V10" stroke="currentColor" stroke-width="1.4" stroke-linecap="round" stroke-linejoin="round"/></svg>',
	trade: '<svg width="22" height="22" viewBox="0 0 20 20" fill="none"><rect x="3.2" y="3.2" width="13.6" height="13.6" rx="2.2" stroke="currentColor" stroke-width="1.4"/><path d="M6.2 12.2V8.6M10 13.4V6.8M13.8 12.2V9.4" stroke="currentColor" stroke-width="1.4" stroke-linecap="round"/></svg>'
}

const COVERS = {
	options: require('@/assets/img/landing/landing-options.jpg'),
	contracts: require('@/assets/img/landing/landing-contracts.jpg'),
	crypto: require('@/assets/img/landing/landing-crypto.jpg'),
	forex: require('@/assets/img/landing/landing-forex.jpg'),
	stocks: require('@/assets/img/landing/landing-stocks.jpg'),
	smart: require('@/assets/img/landing/landing-smart.jpg'),
	markets: require('@/assets/img/landing/landing-markets.jpg'),
	deposit: require('@/assets/img/landing/landing-deposit.jpg'),
	withdraw: require('@/assets/img/landing/landing-withdraw.jpg'),
	orders: require('@/assets/img/landing/landing-orders.jpg'),
	invite: require('@/assets/img/landing/landing-invite.jpg'),
	security: require('@/assets/img/landing/landing-security.jpg'),
	about: require('@/assets/img/landing/landing-about.jpg')
}

function formatPair(code) {
	const c = String(code || '')
	if (c.endsWith('USDT') && c.length > 4) return c.slice(0, -4) + '/USDT'
	if (c.endsWith('USD') && c.length > 3) return c.slice(0, -3) + '/USD'
	if (c.length === 6 && /^[A-Z]+$/.test(c)) return c.slice(0, 3) + '/' + c.slice(3)
	return c
}

function formatPrice(value) {
	const n = Number(value)
	if (!isFinite(n)) return '—'
	if (n >= 1000) return n.toLocaleString(undefined, { maximumFractionDigits: 2 })
	if (n >= 1) return n.toFixed(2)
	return n.toFixed(4)
}

function formatChange(zdf) {
	const n = Number(zdf)
	if (!isFinite(n)) return '0.00%'
	const pct = Math.abs(n) <= 1 ? n * 100 : n
	const sign = pct > 0 ? '+' : ''
	return sign + pct.toFixed(2) + '%'
}

function isUp(zdf) {
	return Number(zdf) >= 0
}

export default {
	name: 'HomeLanding',
	components: { LandingVisual },
	data() {
		return {
			pageReady: false,
			howSteps: [
				{ n: '01', titleKey: 'landing.how1Title', descKey: 'landing.how1Desc', icon: ICONS.account },
				{ n: '02', titleKey: 'landing.how2Title', descKey: 'landing.how2Desc', icon: ICONS.market },
				{ n: '03', titleKey: 'landing.how3Title', descKey: 'landing.how3Desc', icon: ICONS.trade }
			],
			securityItems: [
				{ titleKey: 'landing.secAuditTitle', descKey: 'landing.secAuditDesc', icon: ICONS.audit },
				{ titleKey: 'landing.secIsolateTitle', descKey: 'landing.secIsolateDesc', icon: ICONS.shield },
				{ titleKey: 'landing.secTransparentTitle', descKey: 'landing.secTransparentDesc', icon: ICONS.eye },
				{ titleKey: 'landing.secSupportTitle', descKey: 'landing.secSupportDesc', icon: ICONS.support }
			],
			aboutPoints: ['landing.aboutPoint1', 'landing.aboutPoint2', 'landing.aboutPoint3', 'landing.aboutPoint4'],
			smartPoints: ['landing.smartPoint1', 'landing.smartPoint2', 'landing.smartPoint3'],
			marketPoints: ['landing.marketsPoint1', 'landing.marketsPoint2', 'landing.marketsPoint3']
		}
	},
	computed: {
		...mapState(['isPc', 'isLogin', 'userInfo', 'marketWsList', 'marketTickMap']),
		products() {
			const self = this
			return [
				{
					key: 'options',
					featured: true,
					titleKey: 'landing.productOptionsTitle',
					descKey: 'landing.productOptionsDesc',
					go: function () { self.goMarket() }
				},
				{
					key: 'contracts',
					featured: true,
					titleKey: 'landing.productContractsTitle',
					descKey: 'landing.productContractsDesc',
					go: function () { self.goMarket() }
				},
				{
					key: 'crypto',
					titleKey: 'landing.productCryptoTitle',
					descKey: 'landing.productCryptoDesc',
					go: function () { self.goMarket() }
				},
				{
					key: 'forex',
					titleKey: 'landing.productForexTitle',
					descKey: 'landing.productForexDesc',
					go: function () { self.goMarket() }
				},
				{
					key: 'stocks',
					titleKey: 'landing.productStocksTitle',
					descKey: 'landing.productStocksDesc',
					go: function () { self.goMarket() }
				},
				{
					key: 'smart',
					titleKey: 'landing.productSmartTitle',
					descKey: 'landing.productSmartDesc',
					go: function () { self.goTrading() }
				}
			]
		},
		featuredProducts() {
			return this.products.filter(function (item) { return item.featured })
		},
		marketProducts() {
			return this.products.filter(function (item) { return !item.featured })
		},
		toolProducts() {
			const self = this
			return [
				{
					key: 'deposit',
					titleKey: 'landing.toolDepositTitle',
					descKey: 'landing.toolDepositDesc',
					go: function () { self.goDeposit() }
				},
				{
					key: 'withdraw',
					titleKey: 'landing.toolWithdrawTitle',
					descKey: 'landing.toolWithdrawDesc',
					go: function () { self.goWithdraw() }
				},
				{
					key: 'orders',
					titleKey: 'landing.toolOrdersTitle',
					descKey: 'landing.toolOrdersDesc',
					go: function () { self.goOrders() }
				},
				{
					key: 'invite',
					titleKey: 'landing.toolInviteTitle',
					descKey: 'landing.toolInviteDesc',
					go: function () { self.goInvite() }
				}
			]
		},
		tickerPairs() {
			const map = this.marketTickMap || {}
			const rows = []
			Object.keys(map).forEach((code) => {
				const tick = map[code]
				if (!tick || !tick.code) return
				rows.push({
					code: formatPair(tick.code),
					price: formatPrice(tick.price),
					change: formatChange(tick.zdf),
					up: isUp(tick.zdf)
				})
			})
			return rows.slice(0, 8)
		},
		tickerLoop() {
			if (!this.tickerPairs.length) return []
			const copies = this.tickerPairs.length < 4 ? 4 : 2
			let out = []
			for (let i = 0; i < copies; i++) {
				out = out.concat(this.tickerPairs)
			}
			return out
		},
		floatPills() {
			return this.tickerPairs.slice(0, 2)
		},
		aboutMetrics() {
			return [
				{ labelKey: 'landing.statAssets', value: this.$t('landing.statAssetsValue') },
				{ labelKey: 'landing.statOptionsHint', value: this.$t('landing.productOptionsTitle') },
				{ labelKey: 'landing.statContractsHint', value: this.$t('landing.productContractsTitle') },
				{ labelKey: 'landing.statSupport', value: this.$t('landing.statSupportValue') }
			]
		}
	},
	mounted() {
		setMarketScope({ types: [MARKET_TYPE_CRYPTO], codes: [] })
		this._heroReadyTimer = setTimeout(() => {
			this.pageReady = true
		}, 180)
	},
	beforeDestroy() {
		clearTimeout(this._heroReadyTimer)
		clearMarketScope()
	},
	methods: {
		coverOf(key) {
			return COVERS[key] || ''
		},
		goJoin() {
			if (!requireLogin(this)) return
			this.goMarket()
		},
		goMarket() {
			if (!this.isLogin) {
				requireLogin(this)
				return
			}
			this.$router.push({ name: 'market' }).catch(function () {})
		},
		goTrading() {
			if (!this.isLogin) {
				requireLogin(this)
				return
			}
			this.$router.push({ name: 'trading' }).catch(function () {})
		},
		goDeposit() {
			if (!this.isLogin) {
				requireLogin(this)
				return
			}
			this.$router.push({ name: 'deposit' }).catch(function () {})
		},
		goWithdraw() {
			if (!this.isLogin) {
				requireLogin(this)
				return
			}
			this.$router.push({ name: 'withdraw' }).catch(function () {})
		},
		goOrders() {
			if (!this.isLogin) {
				requireLogin(this)
				return
			}
			this.$router.push({ name: 'orders' }).catch(function () {})
		},
		goInvite() {
			if (!this.isLogin) {
				requireLogin(this)
				return
			}
			this.$router.push({ name: 'invite' }).catch(function () {})
		}
	}
}
</script>

<style lang="scss" scoped>
.landing {
	background: var(--ld-bg, #fff);
	color: var(--ld-text, #111);
	position: relative;
	overflow-x: clip;
}

.landing-page {
	max-width: 1120px;
	margin: 0 auto;
	padding: 0 24px 96px;
	box-sizing: border-box;
}

.landing-sk-overlay {
	position: absolute;
	inset: 0;
	z-index: 2;
	background: var(--ld-bg, #fff);
	max-width: 1120px;
	margin: 0 auto;
	padding: 0 24px 96px;
	box-sizing: border-box;
}

.landing.is-mobile .landing-page {
	padding: 0 16px 0;
}

.hero {
	display: grid;
	grid-template-columns: 1.02fr 1.08fr;
	gap: 40px;
	align-items: center;
	padding: 28px 0 36px;
	min-width: 0;
}

.hero-copy {
	min-width: 0;
}

.eyebrow {
	display: inline-flex;
	align-items: center;
	font-size: 12px;
	font-weight: 600;
	letter-spacing: 0.12em;
	text-transform: uppercase;
	color: var(--ld-accent, #5347e0);
	margin-bottom: 14px;
}

.hero-title {
	margin: 0;
	font-size: 52px;
	font-weight: 700;
	letter-spacing: -0.035em;
	line-height: 1.08;
	color: var(--ld-text, #111);
}

.hero-subtitle {
	margin: 16px 0 0;
	font-size: 16px;
	line-height: 1.55;
	color: var(--ld-text-secondary, #888);
	max-width: 460px;
}

.hero-actions {
	display: flex;
	flex-wrap: wrap;
	gap: 10px;
	margin-top: 28px;
}

.cta-primary,
.cta-ghost {
	display: inline-flex;
	align-items: center;
	justify-content: center;
	min-height: 48px;
	padding: 0 22px;
	border-radius: 999px;
	font-family: inherit;
	font-size: 15px;
	font-weight: 600;
	cursor: pointer;
	user-select: none;
	-webkit-tap-highlight-color: transparent;
}

.cta-primary {
	background: var(--ld-accent, #5347e0);
	color: var(--ld-accent-text, #fff);
	border: 1px solid var(--ld-accent, #5347e0);

	&:hover {
		background: var(--ld-accent-hover, #4338ca);
		border-color: var(--ld-accent-hover, #4338ca);
	}

	&:active {
		opacity: 0.88;
		background: var(--ld-accent, #5347e0) !important;
		border-radius: 999px !important;
	}
}

.cta-ghost {
	background: var(--ld-btn-ghost-bg, #fff);
	color: var(--ld-btn-ghost-text, #111);
	border: 1px solid var(--ld-border, #ececec);

	&:hover {
		border-color: var(--ld-border-strong, #d0d0d0);
		background: var(--ld-bg-muted, #f7f7f7);
	}

	&:active {
		background: var(--ld-bg-muted, #f7f7f7) !important;
		border-radius: 999px !important;
	}
}

.hero-visual {
	position: relative;
	width: 100%;
	max-width: 100%;
	min-width: 0;
	aspect-ratio: 5 / 4;

	::v-deep .lv {
		height: 100%;
	}
}

.float-pill {
	position: absolute;
	z-index: 2;
	min-width: 108px;
	padding: 10px 14px;
	border-radius: 14px;
	background: var(--ld-bg-card, #fff);
	border: 1px solid var(--ld-border, #ececec);
	box-shadow: var(--ld-shadow, 0 16px 48px rgba(0, 0, 0, 0.12));
	animation: pill-float 4.8s ease-in-out infinite;
}

.pill-a {
	left: -10px;
	bottom: 28px;
}

.pill-b {
	right: -6px;
	top: 18px;
	animation-delay: -1.6s;
}

.pill-label {
	display: block;
	font-size: 11px;
	font-weight: 600;
	letter-spacing: 0.04em;
	color: var(--ld-text-secondary, #888);
}

.pill-value {
	display: block;
	margin-top: 2px;
	font-size: 16px;
	font-weight: 700;
	color: var(--ld-text, #111);
	font-variant-numeric: tabular-nums;

	&.is-up { color: var(--ld-success, #16a34a); }
	&.is-down { color: var(--ld-danger, #ef4444); }
}

.ticker {
	overflow: hidden;
	border-top: 1px solid var(--ld-border, #ececec);
	border-bottom: 1px solid var(--ld-border, #ececec);
	margin: 8px -24px 36px;
	padding: 14px 0;
}

.ticker-track {
	display: flex;
	width: max-content;
	animation: ticker 28s linear infinite;
}

.ticker.is-pairs .ticker-track {
	animation-duration: 36s;
	gap: 28px;
	padding: 0 16px;
}

.ticker-copy {
	padding-right: 48px;
	font-size: 13px;
	font-weight: 600;
	letter-spacing: 0.16em;
	text-transform: uppercase;
	color: var(--ld-text-secondary, #888);
	white-space: nowrap;
}

.ticker-pair {
	display: flex;
	align-items: baseline;
	gap: 8px;
	white-space: nowrap;
	font-variant-numeric: tabular-nums;
}

.ticker-code {
	font-size: 13px;
	font-weight: 700;
}

.ticker-price {
	font-size: 13px;
	font-weight: 600;
	color: var(--ld-text-secondary, #888);
}

.ticker-chg {
	font-size: 12px;
	font-weight: 700;

	&.is-up { color: var(--ld-success, #16a34a); }
	&.is-down { color: var(--ld-danger, #ef4444); }
}

.stats {
	display: grid;
	grid-template-columns: repeat(4, 1fr);
	gap: 12px;
	margin-bottom: 56px;
}

.stat-cell {
	padding: 20px 18px;
	border: 1px solid var(--ld-border, #ececec);
	border-radius: 16px;
	background: var(--ld-bg-card, #fff);
}

.stat-value {
	font-size: 22px;
	font-weight: 700;
	letter-spacing: -0.03em;
	font-variant-numeric: tabular-nums;
}

.stat-label {
	margin-top: 6px;
	font-size: 12px;
	font-weight: 600;
	color: var(--ld-text-secondary, #888);
}

.block {
	margin-bottom: 64px;
}

.block-head {
	margin-bottom: 22px;
	max-width: 640px;
}

.block-title {
	margin: 0;
	font-size: 28px;
	font-weight: 700;
	letter-spacing: -0.03em;
	line-height: 1.2;
}

.block-desc {
	margin: 10px 0 0;
	font-size: 15px;
	line-height: 1.55;
	color: var(--ld-text-secondary, #888);
}

.product-featured {
	display: grid;
	grid-template-columns: 1fr 1fr;
	gap: 16px;
	margin-bottom: 16px;
}

.product-grid {
	display: grid;
	grid-template-columns: 1fr 1fr;
	gap: 16px;
}

.product-grid.is-tools {
	grid-template-columns: 1fr 1fr 1fr 1fr;
}

.product-card {
	overflow: hidden;
	border: 1px solid var(--ld-border, #ececec);
	border-radius: 16px;
	background: var(--ld-bg-card, #fff);
	cursor: pointer;
	-webkit-tap-highlight-color: transparent;
	transition: border-color 0.2s ease, background 0.2s ease;

	&:hover {
		border-color: var(--ld-accent, #5347e0);
		background: var(--ld-bg-card, #fff);

		.product-arrow {
			color: var(--ld-accent, #5347e0);
			transform: translateX(3px);
		}
	}

	&:active {
		outline: none;
		box-shadow: none;
		transform: none;
		background: var(--ld-bg-active, #f2f2f2) !important;
		border-radius: 16px !important;
	}
}

.product-media {
	height: 220px;
	overflow: hidden;
	background: var(--ld-bg-muted, #f7f7f7);
}

.product-media.is-compact {
	height: 168px;
}

.product-cover {
	display: block;
	width: 100%;
	height: 100%;
	object-fit: cover;
	transform: scale(1.02);
	transition: transform 0.55s ease;
}

.product-card:hover .product-cover {
	transform: scale(1.06);
}

.product-body {
	padding: 16px 18px 18px;
}

.product-kicker {
	margin-bottom: 6px;
	font-size: 11px;
	font-weight: 600;
	letter-spacing: 0.08em;
	text-transform: uppercase;
	color: var(--ld-text-tertiary, #999);
}

.product-title-row {
	display: flex;
	align-items: center;
	justify-content: space-between;
	gap: 12px;
}

.product-title {
	margin: 0;
	font-size: 18px;
	font-weight: 600;
}

.product-arrow {
	flex-shrink: 0;
	color: var(--ld-text-secondary, #888);
	transition: transform 0.2s ease, color 0.2s ease;
}

.product-desc {
	margin: 8px 0 0;
	font-size: 13px;
	line-height: 1.5;
	color: var(--ld-text-secondary, #888);
}

.how-grid {
	display: grid;
	grid-template-columns: repeat(3, 1fr);
	gap: 12px;
}

.how-card {
	padding: 22px 20px 24px;
	border: 1px solid var(--ld-border, #ececec);
	border-radius: 16px;
	background: var(--ld-bg-card, #fff);
	transition: border-color 0.2s ease;

	&:hover {
		border-color: var(--ld-border-strong, #d0d0d0);
	}
}

.how-icon {
	width: 40px;
	height: 40px;
	border-radius: 12px;
	background: var(--ld-accent-muted, #eef0ff);
	color: var(--ld-accent, #5347e0);
	display: flex;
	align-items: center;
	justify-content: center;
	margin-bottom: 16px;

	::v-deep svg {
		display: block;
	}
}

.how-index {
	font-size: 13px;
	font-weight: 700;
	letter-spacing: 0.08em;
	color: var(--ld-text-tertiary, #999);
	margin-bottom: 10px;
}

.how-title {
	margin: 0;
	font-size: 18px;
	font-weight: 600;
}

.how-desc {
	margin: 8px 0 0;
	font-size: 13px;
	line-height: 1.5;
	color: var(--ld-text-secondary, #888);
}

.markets-block,
.smart-block,
.security-block {
	display: grid;
	grid-template-columns: 1.08fr 0.92fr;
	gap: 36px;
	align-items: center;
}

.markets-media,
.smart-media,
.security-media {
	overflow: hidden;
	border-radius: 16px;
	border: 1px solid var(--ld-border, #ececec);
	min-height: 280px;
	background: var(--ld-bg-muted, #f7f7f7);

	.product-cover {
		min-height: 280px;
	}
}

.security-block {
	grid-template-columns: 0.92fr 1.08fr;
}

.sec-list {
	display: grid;
	gap: 14px;
	margin-top: 22px;
}

.sec-item {
	display: flex;
	gap: 12px;
	align-items: flex-start;
}

.sec-icon {
	flex-shrink: 0;
	width: 36px;
	height: 36px;
	border-radius: 10px;
	background: var(--ld-bg-muted, #f7f7f7);
	color: var(--ld-text, #111);
	display: flex;
	align-items: center;
	justify-content: center;

	::v-deep svg {
		display: block;
	}
}

.sec-title {
	font-size: 14px;
	font-weight: 600;
}

.sec-desc {
	margin-top: 3px;
	font-size: 13px;
	line-height: 1.45;
	color: var(--ld-text-secondary, #888);
}

.text-link {
	display: inline-flex;
	align-items: center;
	margin-top: 20px;
	font-size: 14px;
	font-weight: 600;
	color: var(--ld-accent, #5347e0);
	cursor: pointer;
	border-bottom: 1px solid var(--ld-accent, #5347e0);
	padding-bottom: 1px;
	-webkit-tap-highlight-color: transparent;

	&:active {
		opacity: 0.72;
	}
}

.about-block {
	display: grid;
	grid-template-columns: 1.15fr 0.85fr;
	gap: 36px;
	align-items: start;
}

.about-body {
	margin: 14px 0 0;
	font-size: 15px;
	line-height: 1.65;
	color: var(--ld-text-secondary, #888);
}

.about-points {
	margin: 18px 0 0;
	padding: 0;
	list-style: none;
}

.about-points li {
	position: relative;
	padding-left: 16px;
	margin-bottom: 10px;
	font-size: 14px;
	line-height: 1.5;
	font-weight: 500;

	&::before {
		content: '';
		position: absolute;
		left: 0;
		top: 8px;
		width: 6px;
		height: 6px;
		border-radius: 50%;
		background: var(--ld-text, #111);
	}
}

.about-panel {
	border: 1px solid var(--ld-border, #ececec);
	border-radius: 16px;
	overflow: hidden;
	background: var(--ld-bg-card, #fff);
}

.about-visual {
	height: 200px;
	border-bottom: 1px solid var(--ld-border);
	background: var(--ld-bg-muted);
	overflow: hidden;
}

.about-metric {
	padding: 16px 18px;
	border-bottom: 1px solid var(--ld-border, #ececec);

	&:last-child {
		border-bottom: none;
	}
}

.about-metric-label {
	font-size: 12px;
	font-weight: 600;
	color: var(--ld-text-secondary, #888);
}

.about-metric-value {
	margin-top: 4px;
	font-size: 20px;
	font-weight: 700;
	letter-spacing: -0.03em;
	font-variant-numeric: tabular-nums;
}

.final-cta {
	padding: 36px 32px;
	border-radius: 16px;
	border: 1px solid var(--ld-border, #ececec);
	background: var(--ld-bg-card, #fff);
	color: var(--ld-text, #111);
	text-align: center;
}

.final-title {
	margin: 0;
	font-size: 28px;
	font-weight: 700;
	letter-spacing: -0.03em;
	color: var(--ld-text, #111);
}

.final-desc {
	margin: 10px auto 0;
	max-width: 480px;
	font-size: 15px;
	line-height: 1.5;
	color: var(--ld-text-secondary, #888);
}

.final-actions {
	justify-content: center;
}

@keyframes pill-float {
	0%, 100% { transform: translateY(0); }
	50% { transform: translateY(-8px); }
}

@keyframes ticker {
	from { transform: translateX(0); }
	to { transform: translateX(-50%); }
}

@media (prefers-reduced-motion: reduce) {
	.float-pill,
	.ticker-track,
	.product-arrow,
	.product-cover {
		animation: none !important;
		transform: none !important;
	}
}

html[data-lending-theme='light'] .product-card:hover {
	box-shadow: 0 10px 28px color-mix(in srgb, var(--ld-text, #111) 6%, transparent);
}

html[data-lending-theme='dark'] .product-card:hover,
html[data-lending-theme='dark'] .float-pill {
	box-shadow: none;
}

.landing.is-mobile {
	.hero {
		grid-template-columns: 1fr;
		gap: 24px;
		padding: 12px 0 24px;
	}

	.hero-title {
		font-size: 32px;
	}

	.hero-subtitle {
		font-size: 14px;
		max-width: none;
	}

	.hero-actions {
		margin-top: 20px;
	}

	.cta-primary,
	.cta-ghost {
		flex: 1;
		min-width: 0;
	}

	.hero-visual {
		aspect-ratio: 4 / 3;
		overflow: hidden;
		border-radius: 20px;
	}

	.float-pill {
		min-width: 0;
		padding: 8px 10px;
	}

	.pill-a {
		left: 8px;
		bottom: 12px;
	}

	.pill-b {
		right: 8px;
		top: 12px;
	}

	.pill-value {
		font-size: 14px;
	}

	.ticker {
		margin: 0 -16px 28px;
	}

	.stats {
		grid-template-columns: 1fr 1fr;
		margin-bottom: 40px;
	}

	.stat-value {
		font-size: 18px;
	}

	.block {
		margin-bottom: 44px;
	}

	.block-title {
		font-size: 22px;
	}

	.product-featured,
	.product-grid,
	.product-grid.is-tools,
	.how-grid,
	.markets-block,
	.smart-block,
	.security-block,
	.about-block {
		grid-template-columns: 1fr;
	}

	.product-grid,
	.product-grid.is-tools {
		grid-template-columns: 1fr 1fr;
	}

	.product-media {
		height: 188px;
	}

	.product-media.is-compact {
		height: 156px;
	}

	.final-cta {
		padding: 28px 18px;
		text-align: left;
	}

	.final-title {
		font-size: 24px;
	}

	.final-actions {
		justify-content: stretch;
	}
}
</style>
